/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable-next-line react/prop-types */
/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
/*
Author: Lukman Yudokusumo
*/

import React, { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { View, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import Colors from '@global/colors';
import { formLabel } from '@assets/styles/formStyles';
import FileImage from '@components/form/FileImage';
import ImageUploaderOption from '@components/form/ImageUploaderOption';
import { isWeb } from '@global/screens';
import SociollaErrorMessage from './SociollaErrorMessage';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';

const imageSize = 120;
const maxPhotos = 10;

const ImageLabel = React.memo(({ label, isRequired }) =>
  label ? (
    <SociollaText style={formLabel}>
      {label}{' '}
      {isRequired && (
        <SociollaText style={{ color: Colors.blue0 }}>*</SociollaText>
      )}
    </SociollaText>
  ) : null,
);

const AddImageButton = React.memo(
  ({ testID, canAddImage, onAddImage, setFieldTouched, name, otherProps }) =>
    canAddImage ? (
      <View style={styles.imageContainer}>
        {isWeb ? (
          <FileImage
            testID={testID}
            notRenderLabel
            onBlur={() => setFieldTouched(name)}
            onChange={onAddImage}
            width={120}
            height={120}
            {...otherProps}
          />
        ) : (
          <ImageUploaderOption
            testID={testID}
            value=""
            onChange={async (uploadedUrl) => {
              if (uploadedUrl) {
                await onAddImage(uploadedUrl);
              }
            }}
          />
        )}
      </View>
    ) : null,
);

const ImageItem = React.memo(({ testID, item, index, onRemoveImage, name }) => (
  <View
    testID={`${testID}-item-${index}`}
    key={`${name}-${index}`}
    style={styles.imageContainer}>
    <TouchableOpacity
      testID={`${testID}-remove-button-${index}`}
      onPress={() => onRemoveImage(index)}
      style={styles.removeButton}>
      <Image
        style={{ width: 8, height: 8 }}
        width={8}
        height={8}
        svg
        source={require('@assets/img/icons/close-white.svg')}
      />
    </TouchableOpacity>
    <Image style={styles.imageStyle} source={item} />
  </View>
));

const ImageList = React.memo(({ testID, images, onRemoveImage, name }) =>
  images.length > 0 ? (
    <View style={styles.imageWrapper}>
      {images.map((item, index) => (
        <ImageItem
          testID={testID}
          key={`${name}-${index}`}
          item={item}
          index={index}
          onRemoveImage={onRemoveImage}
          name={`${name}-${index}`}
        />
      ))}
    </View>
  ) : null,
);

const SociollaFileImageList = ({ name, onChange, testID, ...otherProps }) => {
  const { setFieldTouched, setFieldValue, errors, touched, values } =
    useFormikContext();

  const currentImages = useMemo(() => values[name] || [], [values[name]]);
  const canAddImage = useMemo(
    () => currentImages.length < maxPhotos,
    [currentImages],
  );

  const handleAddImage = useCallback(
    async (value) => {
      const newImages = [...currentImages, value];
      await setFieldValue(name, newImages);
      onChange?.(newImages);
    },
    [currentImages, name, onChange, setFieldValue],
  );

  const handleRemoveImage = useCallback(
    async (index) => {
      const newImages = [...currentImages];
      newImages.splice(index, 1);
      await setFieldValue(name, newImages);
      onChange?.(newImages);
    },
    [currentImages, name, onChange, setFieldValue],
  );

  return (
    <View testID={testID} style={{ width: '100%' }}>
      <ImageLabel label={otherProps.label} isRequired={otherProps.isRequired} />
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexDirection: 'row', alignItems: 'center' }}>
        <AddImageButton
          testID={testID}
          canAddImage={canAddImage}
          onAddImage={handleAddImage}
          setFieldTouched={setFieldTouched}
          name={name}
          otherProps={otherProps}
        />
        <ImageList
          testID={`${testID}-image-list`}
          images={currentImages}
          onRemoveImage={handleRemoveImage}
          name={name}
        />
      </ScrollView>
      <SociollaErrorMessage error={errors[name]} visible={touched[name]} />
    </View>
  );
};

const styles = StyleSheet.create({
  imageContainer: {
    marginRight: 20,
  },
  imageWrapper: {
    flexDirection: 'row',
  },
  removeButton: {
    width: 15,
    height: 15,
    borderRadius: 25,
    overflow: 'hidden',
    position: 'absolute',
    top: 5,
    right: 5,
    zIndex: 1,
    elevation: 1,
    backgroundColor: '#0028E4',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageStyle: {
    width: imageSize,
    height: imageSize,
    borderRadius: 8,
  },
});

export default SociollaFileImageList;
